import { useEffect, useState } from "react";
import { useAbility } from "@casl/react";
import { useTranslation } from "react-i18next";
import { Traffic } from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";

import { AbilityContext } from "../../Context/PermissionsContext";

// Components
import TableModel from "../../components/atoms/tables/TableModel";
import { SearchBar } from "../../components/atoms/searchBars/SearchBar"

// Utils
import { RiskControlApi } from "../../services/RiskControlApi";
import { CheckResponse } from "../../utils/checkResponse";

// Styles
import { Container, CustomButtonV2, PageTitle } from "../styles"
import { ShowDialog } from "../../components/ShowDialog";
import { Modal, Grid, useTheme, useMediaQuery } from "@mui/material";
import { RiskControlModalCard } from "../../components/atoms/modalCards/RiskControlModalCard";
import { BackdropLoading } from "../../components/BackdropLoading";
import matrizPng from "../../assets/images/matriz.PNG"

export const RiskList = (props) => {

    const { t } = useTranslation();
    const ability = useAbility(AbilityContext);
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    const riskControlApi = new RiskControlApi();
    const { check } = new CheckResponse();

    const [data, setData] = useState([]);
    const [maxItem, setMaxItem] = useState(0);
    const [loading, setLoading] = useState(false);

    const [filteredRequest, setFilteredRequest] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [openRiskLevelModal, setOpenRiskLevelModal] = useState(false);
    const [detail, setDetail] = useState({});

    const [finalDate, setFinalDate] = useState(
        moment(new Date()).format("YYYY-MM-DD")
    );
    const [initialDate, setInitialDate] = useState(
        moment(finalDate).subtract(1, "months").format("YYYY-MM-DD")
    );
    const [dialog, setDialog] = useState({
        status: false,
        title: "",
        message: "",
        type: null,
    });

    useEffect(() => {
        returnHeaders();

        async function call() {
            await loadData();
        }
        call();
    }, [])

    const returnHeaders = () => {
        let heads = [
            // {
            //     id: "id",
            //     label: "riskLevel.table.id",
            //     isRender: true,
            //     center: false,
            //     sortArrow: true,
            //     sticky: true,
            //     direction: 'left',
            // },
            {
                id: "item",
                label: "riskLevel.table.item",
                isRender: true,
                center: false,
                sortArrow: true
            },
            {
                id: "description",
                label: "riskLevel.table.description",
                isRender: true,
                center: false,
                sortArrow: true
            },
            {
                id: "impactLevel",
                label: "riskLevel.table.impactLevel",
                isRender: true,
                center: false,
                sortArrow: true
            },
            {
                id: "probability",
                label: "riskLevel.table.probability",
                isRender: true,
                center: false,
                sortArrow: true
            },
            {
                id: "riskLevel",
                label: "riskLevel.table.riskLevel",
                isRender: true,
                center: false,
                sortArrow: true
            },
            {
                id: "isActive",
                label: "riskLevel.table.isActive",
                center: false,
                sortArrow: true,
                format: (value) => value.isActive ? t('common.yes') : t('common.no'),
                customSort: "isActive ? 'Si' : 'No'",
            },
        ];

        let newActions = [];
        newActions.push('edit');
        newActions.push('delete');

        // TODO: Ajustar permisos
        // if (ability.can("UpdateRiskLevel")) {
        //     newActions.push('edit');
        // }
        // if (ability.can("DeleteRiskLevel")) {
        //     newActions.push('delete');
        // }

        if (newActions.length > 0) {
            heads.push(addActionsRow(newActions))
        }

        setHeaders(heads);
    }

    function addActionsRow(actions) {
        return {
            id: "actions",
            label: "common.actions.title",
            type: "actions",
            center: false,
            sortArrow: false,
            sticky: true,
            direction: 'right',
            keys: actions,
        }
    }

    async function loadData() {
        setLoading(true);
        let response = await riskControlApi.list(true);
        if (response.status === 200) {
            // setData(response.data);
            const mapped = response.data.map(r => ({
                ...r,
                disabledDelete: !r.isActive   // Remover el botón de papelera
            }));

            setData(mapped);

            // Get max item value
            const max = mapped.length
                ? Math.max(...mapped.map(x => Number(x.item))) + 1
                : 1;

            setMaxItem(max);
        }
        setLoading(false);
    }

    async function handleOpenModalToggleState(row) {
        setDialog({
            status: true,
            title: t('riskLevel.messages.delete.confirm'),
            message: t('riskLevel.messages.delete.confirmMessage', { riskLevel: row.description }),
            type: "confirmation",
            row: row
        });
    }

    async function handleCloseToggleState(confirmToggleState) {
        if (confirmToggleState) {
            setDialog({ ...dialog, status: false });
            await handleToggleState()
        } else {
            setDialog({ status: false });
        }
    }

    async function handleToggleState() {
        if (!dialog.row.id) {
            return
        }

        setLoading(true);
        let response = await riskControlApi.toggleState(dialog.row.id);
        let result = check(response)

        if (result.status) {
            handleCloseToggleState();
            setDialog({
                status: true,
                title: t('riskLevel.messages.delete.title'),
                message: t('riskLevel.messages.delete.message'),
                type: null,
            });
            await loadData();
        }
        else {
            toast.error(() =>
                <div>{t('riskLevel.messages.delete.error')}<br />
                    {result.errors}
                </div>
            );
        }
        setLoading(false);
    }

    async function handleSaveEdit(data) {
        let formData = {
            ...data,
            riskLevel: data.riskLevel.code,
            probability: data.probability.code,
            impactLevel: data.impactLevel.code
        }

        setLoading(true);
        let response = null
        if (formData.id) response = await riskControlApi.update(formData.id, formData);
        else response = await riskControlApi.create(formData);

        let result = check(response)

        if (result.status) {
            setOpenRiskLevelModal(false);
            setDialog({
                status: true,
                title: t('riskLevel.messages.save.title'),
                message: t('riskLevel.messages.save.message'),
                type: null,
            });
            await loadData();
        }
        else {
            toast.error(() =>
                <div>{t('riskLevel.messages.save.error')}<br />
                    {result.errors}
                </div>
            );
        }
        setLoading(false);
    }

    function handleEdit(row) {
        setOpenRiskLevelModal(true)
        setDetail(row)
    }

    function handleCreate() {
        setOpenRiskLevelModal(true)
        setDetail({})
    }

    return (
        <Container open={props.open} style={{ marginBottom: 0 }}>
            <Grid container
                spacing={1}
                alignItems="center"
                sx={{ mb: { md: 2 } }}
                columns={{ xs: 12, sm: 12, md: 12, lg: 13, xl: 15 }}>
                <Grid item xs={12} sm={12} md={12} lg={5} xl={4}>
                    <PageTitle>{t('riskLevel.title')}</PageTitle>
                </Grid>
                <Grid item xs={12} sm={8} md={9} lg={5} xl={6}>
					<SearchBar
						rows={data}
						setFilteredRequest={setFilteredRequest}
						filterText={t('common.searchBars.riskLevel')}
						filterKeys={['item', 'description', 'impactLevel', 'riskLevel']}
					/>
				</Grid>
				{isDesktop &&
					<Grid item md={false} lg={1} xl={3}></Grid>
				}
				<Grid item xs={12} sm={4} md={3} lg={2} xl={2}
            		sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
					<CustomButtonV2
						variant="contained"
						startIcon={<Traffic />}
						aria-label="download"
						onClick={handleCreate}
						btnType="primary"
					>
						{t('riskLevel.modal.create')}
					</CustomButtonV2>
                </Grid>
            </Grid>

            <TableModel
                headers={headers}
                filteredRequest={filteredRequest}
                rows={data}
                // Manage date
                initialDate={initialDate}
                setInitialDate={setInitialDate}
                finalDate={finalDate}
                setFinalDate={setFinalDate}
                // Handle edit, delete, process
                handleEdit={handleEdit}
                handleDelete={handleOpenModalToggleState}
                hideFilterDate={true}
            />

            <ShowDialog
                openDialog={dialog.status}
                dialog={dialog}
                handleCloseDialog={handleCloseToggleState}
            />

            <Modal open={openRiskLevelModal} sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>

				<RiskControlModalCard
					data={detail}
					handleSave={handleSaveEdit}
					handleClose={() => setOpenRiskLevelModal(false)}
					maxItem={maxItem}
					matrizPng={matrizPng}
				/>

            </Modal>

            <BackdropLoading open={loading} />
        </Container>
    )
} 